import { Dispatch, FC, Fragment, SetStateAction, useState } from 'react';
import { generalHelpers, generalHthHelper } from '@monorepo/helpers';
import { geocodingServices, houseToHouseServices, hthHelper } from 'src/services';
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import { googleMapConfig } from 'src/app-config';
import { hideLoadingModalReducer, setAlertResponse, setValuesAndOpenAlertModalReducer, showLoadingModalReducer } from 'src/store';
import { hthMapStyle, HTHMarkerBuilding } from 'src/components/_commons/house-to-house';
import { HTHSectionMapNewBlockOptions } from './HTHSectionMapNewBlockOptions';
import { HTHSectionMapNewFaceOptions } from './HTHSectionMapNewFaceOptions';
import { HTHSectionMapPolygon } from './HTHSectionMapPolygon';
import { Loading } from 'src/components/_commons';
import { typeBlock, typeNewBlockPolygon, typeFace, typeHTHTerritory, typeHTHFace, typeTerritoryNumber } from '@monorepo/models';
import { useDispatch } from 'react-redux';
import { useGetConfig, useGetCurrentUser, useGetIsMobile } from 'src/custom-hooks';

type propsType = {
    currentFace: typeHTHFace | null;
    hthTerritory: typeHTHTerritory;
    isAddingNewBlock: boolean;
    isAddingNewFace: boolean;
    isCompletingNewBlock: boolean;
    isEditingView: boolean;
    selectBlockAndFaceHandler: (block?: typeBlock, face?: typeFace, hthTerritory0?: typeHTHTerritory | null) => void;
    setHTHTerritory: Dispatch<SetStateAction<typeHTHTerritory | null>>;
    setIsAddingNewBlock: Dispatch<SetStateAction<boolean>>;
    setIsAddingNewFace: Dispatch<SetStateAction<boolean>>;
    setIsCompletingNewBlock: Dispatch<SetStateAction<boolean>>;
    setIsEditingView: Dispatch<SetStateAction<boolean>>;
    setShowNewFaceOptions: Dispatch<SetStateAction<boolean>>;
    showNewFaceOptions: boolean;
    territoryNumber: typeTerritoryNumber;
};

export const HTHSectionMap: FC<propsType> = ({
    currentFace, hthTerritory, isAddingNewFace, isCompletingNewBlock,
    isAddingNewBlock, isEditingView, selectBlockAndFaceHandler, setIsAddingNewBlock,
    setIsAddingNewFace, setIsCompletingNewBlock, setIsEditingView, setShowNewFaceOptions,
    setHTHTerritory, showNewFaceOptions, territoryNumber
}) => {
    const [isAddingNewBlockPlus, setIsAddingNewBlockPlus] = useState(false);
    const [map, setMap] = useState<google.maps.Map | null>(null);
    const [runIntervals, setRunIntervals] = useState(false);
    const config = useGetConfig();
    const dispatch = useDispatch();
    const isMobile = useGetIsMobile();
    const mapLoader = useJsApiLoader(googleMapConfig);
    const user = useGetCurrentUser();

    const onCenterChangedHandler = (): void => {
        const lat = map?.getCenter()?.lat() ?? 0;
        const lng = map?.getCenter()?.lng() ?? 0;
        if (!lat || !lng) {
            return;
        }
        if (lat === hthTerritory.centerCoords.lat && lng === hthTerritory.centerCoords.lng) {
            return;
        }
        const currentTerritoryHTH = { ...hthTerritory };
        currentTerritoryHTH.centerCoords.lat = lat;
        currentTerritoryHTH.centerCoords.lng = lng;
        setHTHTerritory(currentTerritoryHTH);
    }

    const onZoomChangedHandler = (): void => {
        const newZoom = map?.getZoom() ?? 0;
        if (!newZoom || newZoom === hthTerritory.zoom) {
            return;
        }
        const currentTerritoryHTH = { ...hthTerritory };
        currentTerritoryHTH.zoom = newZoom;
        setHTHTerritory(currentTerritoryHTH);
    }

    const initMapViewEditingHandler = (): void => {
        dispatch(setValuesAndOpenAlertModalReducer({
            mode: 'confirm',
            title: '¿Modificar este mapa?',
            message: `El mapa del territorio ${hthTerritory.territoryNumber} se va a guardar como se ve ahora`,
            execution: editMapViewHandler
        }));
    }

    const editMapViewHandler = async (): Promise<void> => {
        // const editedHTHMap = { ...hthTerritory.map };
        const editedHTHMap = { ...hthTerritory };
        dispatch(showLoadingModalReducer());
        const initResponse = await houseToHouseServices.GetTerritory(hthTerritory.territoryNumber);
        dispatch(hideLoadingModalReducer());
        const unmodifiedHTHTerritory = initResponse.data?.hthTerritory ?? null;
        if (!unmodifiedHTHTerritory) {
            return;
        }
        const editedHTHPolygons: typeHTHFace[] = [];
        editedHTHMap.faces.forEach((x, index) => {
            const unP = unmodifiedHTHTerritory.faces[index];
            if (x.coordsPoint1.lat !== unP.coordsPoint1.lat || x.coordsPoint1.lng !== unP.coordsPoint1.lng
                || x.coordsPoint2.lat !== unP.coordsPoint2.lat || x.coordsPoint2.lng !== unP.coordsPoint2.lng
                || x.coordsPoint3.lat !== unP.coordsPoint3.lat || x.coordsPoint3.lng !== unP.coordsPoint3.lng
            ) {
                editedHTHPolygons.push(x);
            }
        });
        if (!editedHTHMap.centerCoords.lat || !editedHTHMap.centerCoords.lng || !editedHTHMap.faces || !editedHTHMap.zoom) {
            dispatch(setValuesAndOpenAlertModalReducer({
                mode: 'alert',
                title: 'Algo falló en el mapa',
                message: `Algo falló en los datos al querer modificar el mapa del territorio ${hthTerritory.territoryNumber}; refrescar la página e intentar de nuevo`
            }));
            setIsEditingView(false);
            return;
        }
        dispatch(showLoadingModalReducer());
        const response = await houseToHouseServices.EditMap(hthTerritory.territoryNumber, editedHTHMap.centerCoords, editedHTHMap.zoom, editedHTHPolygons);
        dispatch(hideLoadingModalReducer());
        if (!response.success) {
            dispatch(setAlertResponse({ response }));
            return;
        }
        reloadHandler();
    }

    const initFaceAddingHandler = (selectedBlock: typeBlock | null = null, selectedFace: typeFace | null = null, selectedStreet: string | null = null): void => {
        setIsAddingNewFace(true);
        setShowNewFaceOptions(false);
        const currentPolygon = hthTerritory.faces.find(x => x.id === 0);
        if (!selectedBlock || !selectedFace || !selectedStreet || currentPolygon) {
            return;
        }
        const polygon: typeHTHFace = {
            id: 0,
            block: selectedBlock,
            buildings: [],
            coordsPoint1: {
                lat: hthTerritory.centerCoords.lat + 0.001,
                lng: hthTerritory.centerCoords.lng
            },
            coordsPoint2: {
                lat: hthTerritory.centerCoords.lat - 0.0003827,
                lng: hthTerritory.centerCoords.lng + 0.0009239
            },
            coordsPoint3: {
                lat: hthTerritory.centerCoords.lat - 0.0003827,
                lng: hthTerritory.centerCoords.lng - 0.0009239
            },
            dateOfLastSharing: null,
            doNotCalls: [],
            face: selectedFace,
            observations: [],
            serviceDates: [],
            street: selectedStreet
        };
        const currentTerritory = { ...hthTerritory };
        currentTerritory.faces = [...currentTerritory.faces, polygon];
        setHTHTerritory(currentTerritory);
    }

    const initBlockAddingHandler = (block: typeBlock, numberOfFaces = 4): void => {
        setShowNewFaceOptions(false);
        const newBlockPolygon: typeNewBlockPolygon = {
            block,
            coordinates: [
                {
                    lat: hthTerritory.centerCoords.lat + 0.001,
                    lng: hthTerritory.centerCoords.lng - 0.001
                },
                {
                    lat: hthTerritory.centerCoords.lat + 0.001,
                    lng: hthTerritory.centerCoords.lng + 0.001
                },
                {
                    lat: hthTerritory.centerCoords.lat - 0.001,
                    lng: hthTerritory.centerCoords.lng + 0.001
                },
                {
                    lat: hthTerritory.centerCoords.lat - 0.001,
                    lng: hthTerritory.centerCoords.lng - 0.001
                }
            ],
            polygons: []
        };
        const currentTerritory: typeHTHTerritory = { ...hthTerritory, newBlockPolygon };
        setHTHTerritory(currentTerritory);
    }

    const acceptBlockHandler = () => {
        const newBlockPolygon = { ...hthTerritory.newBlockPolygon };
        if (!newBlockPolygon?.coordinates || !newBlockPolygon.block) {
            return;
        }
        if (isAddingNewBlockPlus) {
            dispatch(setValuesAndOpenAlertModalReducer({
                mode: 'confirm',
                title: "Confirmar",
                message: `Se van a agregar estas 4 caras correspondientes a la manzana ${generalHthHelper.MaskTheBlock(newBlockPolygon.block, config.isUsingLettersForBlocks)}`,
                execution: async () => {
                    const sortedCoordinates = hthHelper.SortCoordinatesClockwise(newBlockPolygon.coordinates!);
                    const faces: typeHTHFace[] = [];
                    const promises = Array.from({ length: 4 }, async (_, i) => {
                        const nextIndex = i < 3 ? i + 1 : 0;
                        const middlePointCoordinates = hthHelper.GetPolygonCenter([sortedCoordinates[i], sortedCoordinates[nextIndex]]);
                        const responseStreet = await geocodingServices.GetStreetFromCoordinates(middlePointCoordinates);
                        if (!responseStreet.success || !responseStreet.data?.street) {
                            throw new Error("Sin calle");
                        }
                        const newPolygon: typeHTHFace = {
                            block: newBlockPolygon.block!,
                            buildings: [],
                            coordsPoint1: hthHelper.GetPolygonCoordinates(1, i + 1, sortedCoordinates),
                            coordsPoint2: hthHelper.GetPolygonCoordinates(2, i + 1, sortedCoordinates),
                            coordsPoint3: hthHelper.GetPolygonCoordinates(3, i + 1, sortedCoordinates),
                            dateOfLastSharing: null,
                            doNotCalls: [],
                            face: i === 0 ? 'B' : i === 1 ? 'C' : i === 2 ? 'D' : 'A',
                            id: 0,
                            observations: [],
                            serviceDates: [],
                            street: responseStreet.data.street
                        };
                        faces.push(newPolygon);
                    });
                    dispatch(showLoadingModalReducer());
                    try {
                        await Promise.all(promises);
                    } catch (err) {
                        dispatch(setValuesAndOpenAlertModalReducer({
                            mode: 'alert',
                            title: "Algo falló",
                            message: "No se pudo conseguir alguna de las calles. Ubicar bien los puntos en las esquinas de la manzana. Si el problema persiste, usar la otra opción para cargar la manzana que permite elegir los nombres de calle manualmente.",
                            animation: 2
                        }));
                        return;
                    } finally {
                        dispatch(hideLoadingModalReducer());
                    }
                    const response = await houseToHouseServices.AddPolygonFacesToTerritory(hthTerritory.territoryNumber, faces);
                    if (!response.success) {
                        dispatch(setAlertResponse({ response }));
                        return;
                    }
                    reloadHandler();
                }
            }));
        } else {
            const newPolygons: typeHTHFace[] = [];
            newBlockPolygon.coordinates.forEach((_, i) => {
                const newPolygon: typeHTHFace = {
                    block: newBlockPolygon.block!,
                    buildings: [],
                    color: i === 0 ? 'yellow' : i === 1 ? 'green' : i === 2 ? 'red' : 'blue',
                    coordsPoint1: hthHelper.GetPolygonCoordinates(1, i + 1, newBlockPolygon.coordinates!),
                    coordsPoint2: hthHelper.GetPolygonCoordinates(2, i + 1, newBlockPolygon.coordinates!),
                    coordsPoint3: hthHelper.GetPolygonCoordinates(3, i + 1, newBlockPolygon.coordinates!),
                    dateOfLastSharing: null,
                    doNotCalls: [],
                    face: 'x',
                    id: 0,
                    observations: [],
                    serviceDates: [],
                    street: ''
                };
                newPolygons.push(newPolygon);
            });
            const currentTerritory = {
                ...hthTerritory,
                polygons: [...hthTerritory.faces, ...newPolygons]
            };
            currentTerritory.newBlockPolygon = undefined;
            setHTHTerritory(currentTerritory);
            setIsCompletingNewBlock(true);
        }
    }

    const addFaceHandler = async (): Promise<void> => {
        const newPolygon = hthTerritory.faces.find(x => x.id === 0);
        if (!newPolygon) {
            return;
        }
        dispatch(showLoadingModalReducer());
        const response = await houseToHouseServices.AddPolygonFacesToTerritory(hthTerritory.territoryNumber, [newPolygon]);
        dispatch(hideLoadingModalReducer());
        if (!response.success) {
            dispatch(setAlertResponse({ response }));
            return;
        }
        reloadHandler();
    }

    const cancelChangesHandler = (): void => {
        dispatch(setValuesAndOpenAlertModalReducer({
            mode: 'confirm',
            title: '¿Cancelar cambios en el mapa?',
            message: `Los cambios hechos en el mapa ${hthTerritory.territoryNumber} se eliminarán`,
            execution: () => window.location.reload()
        }));
    }

    const reloadHandler = (): void => {
        setIsAddingNewFace(false);
        setIsAddingNewBlock(false);
        setIsAddingNewBlockPlus(false);
        setIsEditingView(false);
        setRunIntervals(false);
        setShowNewFaceOptions(false);
        setIsCompletingNewBlock(false);
    }

    if (!mapLoader.isLoaded) {
        return <Loading />;
    }

    return (
        <>
        <div className={'position-relative'} >
            <GoogleMap
                id={googleMapConfig.id}
                mapContainerClassName={'d-block m-auto'}
                mapContainerStyle={{
                    height: isMobile ? '600px' : '500px',
                    width: isMobile ? '100%' : '90%'
                }}
                onLoad={mapInstance => {
                    setMap(mapInstance);
                    mapInstance.setCenter(hthTerritory.centerCoords);
                    mapInstance.setZoom(hthTerritory.zoom);
                }}
                onCenterChanged={onCenterChangedHandler}
                options={{
                    disableDefaultUI: false,
                    draggable: isMobile || isEditingView,
                    fullscreenControl: isEditingView,
                    fullscreenControlOptions: { position: google.maps.ControlPosition.RIGHT_CENTER },
                    isFractionalZoomEnabled: true,
                    mapTypeControl: false,
                    minZoom: 8,
                    panControl: true,
                    streetViewControl: !isEditingView,
                    styles: hthMapStyle,
                    zoomControl: isEditingView,
                    zoomControlOptions: { position: google.maps.ControlPosition.LEFT_BOTTOM }
                }}
                onZoomChanged={onZoomChangedHandler}
            >
                {/* Polygons */}
                {!!hthTerritory.faces?.length && hthTerritory.faces.map(face =>
                    <Fragment key={face.id}>
                        <HTHSectionMapPolygon
                            currentFace={currentFace}
                            hthTerritory={hthTerritory}
                            isAddingPolygon={isAddingNewFace}
                            isEditingView={isEditingView}
                            polygon={face}
                            runIntervals={runIntervals}
                            selectBlockAndFaceHandler={selectBlockAndFaceHandler}
                            setHTHTerritory={setHTHTerritory}
                            territoryNumber={territoryNumber}
                        />
                        {!isAddingNewBlock && !isAddingNewBlockPlus && !isAddingNewFace && !isCompletingNewBlock && !isEditingView
                         && currentFace?.buildings.filter(b => b.coords && b.households.some(h => h.history.filter(x => !!x.state).map(x => x.timestamp).some(d => generalHelpers.IsToday(d)))).map((b, i) =>
                            <HTHMarkerBuilding key={i}
                                building={b}
                            />
                        )}
                    </Fragment>
                )}
                {/* New block */}
                {hthTerritory.newBlockPolygon &&
                    <HTHSectionMapPolygon
                        currentFace={null}
                        hthTerritory={hthTerritory}
                        isAddingPolygon={true}
                        isEditingView={false}
                        polygon={{
                            block: hthTerritory.newBlockPolygon.block,
                            buildings: [],
                            coordsPoint1: hthTerritory.newBlockPolygon.coordinates[0],
                            coordsPoint2: hthTerritory.newBlockPolygon.coordinates[1],
                            coordsPoint3: hthTerritory.newBlockPolygon.coordinates[2],
                            coordsPoint4: hthTerritory.newBlockPolygon.coordinates[3],
                            dateOfLastSharing: null,
                            doNotCalls: [],
                            face: 'x',
                            id: 0,
                            observations: [],
                            serviceDates: [],
                            street: ''
                        }}
                        runIntervals={runIntervals}
                        selectBlockAndFaceHandler={selectBlockAndFaceHandler}
                        setHTHTerritory={setHTHTerritory}
                        territoryNumber={territoryNumber}
                    />
                }
            </GoogleMap>
        </div>
        {(isAddingNewFace || showNewFaceOptions) &&
            <HTHSectionMapNewFaceOptions
                hthTerritory={hthTerritory}
                initFaceAddingHandler={initFaceAddingHandler}
                show={isAddingNewFace && showNewFaceOptions}
            />
        }
        {isAddingNewBlock &&
            <HTHSectionMapNewBlockOptions
                hthTerritory={hthTerritory}
                initBlockAddingHandler={initBlockAddingHandler}
                isCompletingNewBlock={isCompletingNewBlock}
                reloadHandler={reloadHandler}
                setRunIntervals={setRunIntervals}
            />
        }
        {user.isHthAdmin && !isMobile &&
            <div className={'d-flex justify-content-center'}>
                {!config.isDisabledEditHthMaps && !isAddingNewFace && !isEditingView && !isAddingNewBlock &&
                    <>
                    <button className={'btn btn-general-blue mt-4 me-4'}
                        onClick={() => {
                            selectBlockAndFaceHandler();
                            setIsEditingView(true);
                            setRunIntervals(true);
                        }}
                    >
                        Editar Mapa
                    </button>
                    <button className={'btn btn-general-blue mt-4 me-4'}
                        onClick={() => {
                            setIsAddingNewBlockPlus(true);
                            setIsAddingNewBlock(true);
                        }}
                    >
                        Agregar Manzana Autocarga
                    </button>
                    <button className={'btn btn-general-blue mt-4 me-4'}
                        onClick={() => setIsAddingNewBlock(true)}
                    >
                        Agregar Manzana
                    </button>
                    <button className={'btn btn-general-blue mt-4'}
                        onClick={() => {
                            selectBlockAndFaceHandler()
                            setIsAddingNewFace(true)
                            setRunIntervals(true)
                            setShowNewFaceOptions(true)
                        }}
                    >
                        Agregar Cara
                    </button>
                    </>
                }
                {isAddingNewFace && !showNewFaceOptions &&
                    <button className={'btn btn-general-red btn-size12 mt-4'} onClick={() => addFaceHandler()}>
                        Guardar Cambios
                    </button>
                }
                {isAddingNewBlock && hthTerritory.newBlockPolygon &&
                    <button className={'btn btn-general-blue mt-4'} onClick={() => acceptBlockHandler()}>
                        Aceptar Manzana Nueva
                    </button>
                }
                {isEditingView &&
                    <button className={'btn btn-general-red btn-size12 mt-4 me-4'} onClick={() => initMapViewEditingHandler()}>
                        Guardar Cambios
                    </button>
                }
                {(isEditingView || isAddingNewFace || isAddingNewBlock) &&
                    <button className={`btn btn-secondary btn-size12 mt-4 ${showNewFaceOptions ? '' : 'mx-4'}`}
                        onClick={() => cancelChangesHandler()}
                    >
                        Cancelar Cambios
                    </button>
                }
            </div>
        }
        </>
    );
}
